import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { Tab, TabBar } from './tabStyles';

export interface TabItem {
  title: string;
  to: string;
  params?: Record<string, string>;
  icon?: IconDefinition;
}

export interface TabsBarProps {
  tabs: TabItem[];
  base?: string;
  selected?: string;
  onTabClick?: (tab: TabItem) => void;
  tabLevel?: number;
}

export function TabsBar({
  tabs,
  base = '',
  selected,
  onTabClick,
  tabLevel,
}: TabsBarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const normalizedPath = currentPath.replace(/\/$/, '');

  const level =
    typeof tabLevel === 'number'
      ? tabLevel
      : base
        ? base.split('/').filter(Boolean).length + 1
        : 1;

  return (
    <TabBar>
      {tabs.map((tab) => {
        const pathSegments = normalizedPath.split('/').filter(Boolean);
        const remainingPath = pathSegments.slice(level - 1).join('/');

        const isActive =
          selected !== undefined
            ? selected === tab.to
            : level === 0
              ? normalizedPath === tab.to ||
                normalizedPath.startsWith(`${tab.to}/`)
              : remainingPath === tab.to ||
                remainingPath.startsWith(`${tab.to}/`);

        return (
          <Tab
            key={tab.to}
            selected={isActive}
            onClick={() => {
              if (onTabClick) {
                onTabClick(tab);
              } else {
                navigate({
                  to: base ? `${base}/${tab.to}` : tab.to,
                  ...(tab.params && { params: tab.params }),
                });
              }
            }}
          >
            {tab.icon && <FontAwesomeIcon icon={tab.icon} />}
            {tab.title}
          </Tab>
        );
      })}
    </TabBar>
  );
}
